import { BlockId } from './block';

/**
 * Tip ID should be an i18n property (en.json & ja.json) under minigame.match2.tips
 * Tests will ensure that all tips are available in both languages, so the type can be a plain string.
 */
export type TipId = string;

/**
 * Map of block IDs to tip IDs. Blocks that should not have tips should be defined with an empty string.
 */
export const blockToTip: Record<BlockId, TipId> = {
  // Basic blocks
  basicBlue: '',
  basicGreen: '',
  basicPurple: '',
  basicRed: '',
  basicYellow: '',
  cubeBlue: '',
  cubeGreen: '',
  cubePurple: '',
  cubeRed: '',
  cubeYellow: '',
  randomSpawn: '',
  bomb: '',
  rocketHorizontal: '',
  rocketVertical: '',

  // Special blocks
  android: 'puzzleAndroid',
  animal: 'puzzleAnimal',
  cage: 'puzzleCage',
  candle: 'puzzleCandle',
  chain: 'puzzleChain',
  chameleon: 'puzzleChameleon',
  dog: 'puzzleDog',
  doorAll: 'puzzleDoor',
  doorBlue: 'puzzleColorDoor',
  doorGreen: 'puzzleColorDoor',
  doorPurple: 'puzzleColorDoor',
  doorRed: 'puzzleColorDoor',
  doorYellow: 'puzzleColorDoor',
  firebox: 'puzzleFirebox',
  fireboxLollipop: 'puzzleFirebox',
  fugu: 'puzzleFugu',
  miniFugu: '', // Second stage of the fugu
  hatBlue: 'puzzleColorHat',
  hatGreen: 'puzzleColorHat',
  hatPurple: 'puzzleColorHat',
  hatRed: 'puzzleColorHat',
  hatYellow: 'puzzleColorHat',
  hatAll: 'puzzleHats',
  hazard: 'puzzleHazard',
  virus: '', // Second stage of the hazard
  kappa: 'puzzleKappa',
  kettle: 'puzzleKettle',
  lock: 'puzzleLock',
  pantherLarge: 'puzzlePanther',
  pantherSmall: 'puzzlePanther',
  pipe: 'puzzlePipe',
  siren: 'puzzleSiren',
  skull: 'puzzleSkull',
  slime: 'puzzleSlime',
  statue: 'puzzleStatue',
  thrower: 'puzzleThrower',

  // New blocks
  coffin: 'puzzleCoffin',
  zombie: '', // Second stage of coffin
  spider: 'puzzleSpider',
  thickIce: 'puzzleIce',
  fullIce: 'puzzleIce',
  halfIce: 'puzzleIce',
  ice: 'puzzleIce',
  curtainAll: 'puzzleCurtain',
  curtainBlue: 'puzzleCurtain',
  curtainGreen: 'puzzleCurtain',
  curtainPurple: 'puzzleCurtain',
  curtainRed: 'puzzleCurtain',
  curtainYellow: 'puzzleCurtain',
  worm: 'puzzleWorm',
  cocoon: '', // Second stage of the worm
  butterfly: '', // Third stage of the worm
  pond: 'puzzlePond',
  candyBag: 'puzzleCandyBag',
  candy: '', // Second stage of the candy bag
  shell: 'puzzleShell',
  sodaBlue: 'puzzleSoda',
  sodaGreen: 'puzzleSoda',
  sodaPurple: 'puzzleSoda',
  sodaRed: 'puzzleSoda',
  sodaYellow: 'puzzleSoda',
  bubbles: 'puzzleBubbles',
  alarm: 'puzzleAlarm',
  chocolateEgg: 'puzzleChocolateEgg',
  nest: 'puzzleNest',
  mushroom: 'puzzleMushroom',
  wall: 'puzzleWall',
  parcels: 'puzzleParcels',
  fossil: 'puzzleFossil',
  corn: 'puzzleCorn',
  popcorn: '', // Second stage of the corn
  tunaCan: 'puzzleTunaCan',
  tunaFish: '', // Second stage of the tuna can
  rainbowBubble: 'puzzleRainbowBubble',
  grass: 'puzzleGrass',
  firecrackerBox: 'puzzleFirecrackerBox',
  cooler: 'puzzleCooler',
  tvBlue: 'puzzleTv',
  tvGreen: 'puzzleTv',
  tvPurple: 'puzzleTv',
  tvRed: 'puzzleTv',
  tvYellow: 'puzzleTv',
  tvAll: 'puzzleTv',
  shieldBubble: 'puzzleShieldBubble',
  bathtub: 'puzzleBathtub',
  sharpenerLeft: 'puzzleSharpener',
  sharpenerRight: 'puzzleSharpener',
  sharpenerTop: 'puzzleSharpener',
  sharpenerBottom: 'puzzleSharpener',
  barrel: 'puzzleBarrel',
  rainbowSoap: 'puzzleRainbowSoap',
  bigEgg: 'puzzleBigEgg',
  piggyBank: 'puzzlePiggyBank',
  balloonBox: 'puzzleBalloonBox',
  octopus: 'puzzleOctopus',
  flowerFidget: 'puzzleFlowerFidget',
  magicBunnies: 'puzzleMagicBunnies',
  laserBoxHorizontal: 'puzzleLaserBox',
  laserBoxVertical: 'puzzleLaserBox',
};

/**
 * Get the tip ID for a block ID. The tip ID will be empty if the block does not have a tip.
 */
export function match2GetTipId(blockId: BlockId): TipId {
  return blockToTip[blockId];
}
